import { useCallback } from 'react';

import {
  CircleHelpIcon,
  CirclePlusIcon,
  Clock3Icon,
  FlagIcon,
  InfoIcon,
  LocateFixedIcon,
} from 'lucide-react';
import { useDrag } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { NavLink } from 'react-router';

import IconizedDialog from '@/components/atoms/IconizedDialog/IconizedDialog';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { cn } from '@/lib/utils';
import { useDialogReminder } from '@/modules/aiAgents/hooks/useDialogReminder';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { Presence } from '@/shared/types/presence';
import { RootState } from '@/shared/types/state';

import { SubnavType } from '../SubNav';

import styles from './TreeView.module.scss';

export interface ItemProps {
  id: string;
  link: string;
  name: string;
  collection: string;
  hasSuggestions?: boolean;
  isOver?: boolean;
  isOrphan?: boolean;
  needsAction?: boolean;
  whoIsViewing?: Presence[];
  selected: boolean;
  type?: 'intent' | 'event' | 'unknown';
  subnavType?: SubnavType;
  isStartingDialog?: boolean;
}
export const renderDialogIcon = (type: string) => {
  if (type === 'intent') {
    return <LocateFixedIcon size={16} color="currentColor" />;
  }
  if (type === 'event') {
    return <FlagIcon size={16} color="currentColor" />;
  }
  if (type === 'unknown') {
    return <CircleHelpIcon size={16} color="currentColor" />;
  }
  // This is a workaround to display the correct icon for the create sample dialog
  if (type === 'create_sample_dialog') {
    return <CirclePlusIcon size={16} color="currentColor" />;
  }
  return null;
};

function Item({
  name,
  id,
  link,
  collection,
  hasSuggestions = false,
  needsAction,
  isOrphan,
  whoIsViewing = [],
  selected,
  type,
  subnavType,
  isStartingDialog,
}: ItemProps) {
  const { t } = useTranslation();

  // Only check for reminders when TreeView is showing dialogs, since reminders are only associated with them
  const { hasReminders } = useDialogReminder(subnavType === 'dialogs' ? id : undefined);

  const [{ isDragging }, dragRef] = useDrag({
    item: { id, name, collection },
    type: 'treeItem',
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });
  const isOffTopics = id === 'offtopics';
  const isRecommendation = collection === t('intent.recommendations.collection');

  const canWrite = useSelector(
    (state: RootState) => getPermissions(state, 'brains', actions.WRITE),
    shallowEqual
  );

  const showDot = !isRecommendation && (hasSuggestions || needsAction);

  const dotColor = useCallback(() => {
    if (hasSuggestions) {
      return 'text-muted-foreground';
    }
    if (needsAction && subnavType === 'intents') {
      return 'text-warning';
    }
    return '';
  }, [hasSuggestions, subnavType, needsAction]);

  return (
    <div
      ref={(node) => {
        if (canWrite) {
          dragRef(node);
        }
      }}
      className={cn(styles.treeItem, { [styles.isDragging]: isDragging })}
    >
      <NavLink
        className={({ isActive }) =>
          cn(styles.link, {
            [styles.active]: isActive,
            [styles.isOrphan]: isOrphan,
            [styles.selected]: selected,
            [styles.hasIcon]: subnavType === 'dialogs',
          })
        }
        end
        to={link}
        title={name}
      >
        <IconizedDialog
          dialogName={name}
          type={type}
          showDot={showDot}
          isOffTopics={isOffTopics}
          whoIsViewing={whoIsViewing}
          dotColor={dotColor}
        />

        {hasReminders && (
          <InfoTooltip content={t('ai_agents.reminder.assigned_to_reminder')} showArrow>
            <Clock3Icon size="16" />
          </InfoTooltip>
        )}

        {isStartingDialog && (
          <InfoTooltip content={t('ai_agents.conversation_start.dialog_tooltip')} showArrow>
            <InfoIcon size="16" />
          </InfoTooltip>
        )}
      </NavLink>
    </div>
  );
}
export default Item;
